/**
 * <product-material>
 * ---------------------------------------------------------------------------
 * Keeps the swatch and texture images in sync with the selected colour, and
 * drives the two side drawers via native <dialog>.
 *
 * Variant detection is deliberately defensive. Themes update the hidden
 * `input[name="id"]` by property assignment, which fires no event, so a
 * `change` listener on that input never runs. Instead we sample the form after
 * any user interaction, on a short schedule that outlasts the theme's own
 * async update, and fall back to reading the selected colour straight out of
 * the DOM. Nothing here depends on a specific theme's event contract.
 */

const VARIANT_EVENTS = [
  "variant:change",
  "variant:changed",
  "on:variant:selected",
  "product:variant-change",
];

// Themes settle the hidden input at wildly different moments — some
// synchronously, some after a section fetch. Sampling beats guessing.
const SAMPLE_DELAYS = [0, 60, 200, 500, 900];

const MOTION_SAFE = !window.matchMedia("(prefers-reduced-motion: reduce)")
  .matches;

class ProductMaterial extends HTMLElement {
  #data = { colours: {}, colourList: [], byValueId: {}, variants: {} };
  #colorPosition = 0;
  #currentColour = null;
  #lastVariantId = null;
  #form = null;
  #observer = null;
  #timers = [];
  #abort = new AbortController();
  #rerenders = false;
  #inflight = null;

  connectedCallback() {
    const dataEl = this.querySelector("[data-material-data]");
    if (dataEl) {
      try {
        this.#data = {
          colours: {},
          colourList: [],
          byValueId: {},
          variants: {},
          ...JSON.parse(dataEl.textContent),
        };
      } catch (error) {
        console.warn("[product-material] Malformed lookup table", error);
      }
    }

    this.#colorPosition = Number(this.dataset.colorPosition || 0);
    this.#rerenders = this.dataset.rerender === "true";

    const { signal } = this.#abort;

    this.addEventListener("click", this.#onClick, { signal });

    this.querySelectorAll("[data-material-drawer]").forEach((dialog) => {
      dialog.addEventListener("cancel", this.#onCancel, { signal });
      dialog.addEventListener("click", this.#onDialogClick, { signal });
    });

    VARIANT_EVENTS.forEach((name) => {
      document.addEventListener(name, this.#onVariantEvent, { signal });
    });

    // Prestige's swatches are real radio inputs, so a user selection always
    // fires `change` on them. This is exact and instant — no timers involved.
    document.addEventListener("change", this.#onOptionChange, { signal });

    this.#bindForm();
  }

  disconnectedCallback() {
    this.#timers.forEach(clearTimeout);
    this.#timers = [];
    this.#observer?.disconnect();
    this.#inflight?.abort();
    this.#abort.abort();
    document.documentElement.classList.remove("material-drawer-open");
  }

  /* ------------------------------------------------------------ form watch */

  #bindForm() {
    const { signal } = this.#abort;

    this.#form =
      this.closest('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]');

    // Real user interaction always fires these, whatever the theme does next.
    const scope = this.#form || document;
    scope.addEventListener("change", this.#scheduleSample, { signal });
    scope.addEventListener("click", this.#scheduleSample, { signal });

    const idInput = this.#idInput();
    this.#lastVariantId = idInput?.value ?? null;

    // Catches themes that write via setAttribute rather than the property.
    if (idInput) {
      this.#observer = new MutationObserver(this.#sample);
      this.#observer.observe(idInput, {
        attributes: true,
        attributeFilter: ["value"],
      });
    }
  }

  #idInput() {
    return (
      this.#form?.querySelector('input[name="id"], select[name="id"]') ||
      document.querySelector('form[action*="/cart/add"] [name="id"]')
    );
  }

  #scheduleSample = () => {
    this.#timers.forEach(clearTimeout);
    this.#timers = SAMPLE_DELAYS.map((ms) => setTimeout(this.#sample, ms));
  };

  #sample = () => {
    const id = this.#idInput()?.value ?? null;
    const changed = Boolean(id) && id !== this.#lastVariantId;
    if (changed) this.#lastVariantId = id;

    // When the material itself varies per variant, ANY variant change matters —
    // style, model, colour. Keying off the colour alone would miss a Vintage ->
    // Metro switch that swaps the leather entirely.
    if (this.#rerenders) {
      if (changed) this.#rerender(id);
      return;
    }

    if (changed) {
      const index = this.#data.variants?.[id];
      const colour = index == null ? null : this.#data.colourList?.[index];
      if (colour) {
        this.#apply(colour);
        return;
      }
    }

    // Size-independent fallback: read the selection out of the picker itself.
    const fromDom = this.#colourFromDom();
    if (fromDom) this.#apply(fromDom);
  };

  #onOptionChange = (event) => {
    if (this.#rerenders) return;

    const el = event.target;
    if (!el?.matches?.('input[type="radio"]')) return;
    if (!this.#isColourInput(el)) return;

    const colour = this.#data.byValueId?.[String(el.value)];
    if (colour) this.#apply(colour);
  };

  /**
   * An input belongs to the colour option if either signal matches. Both come
   * straight out of variant-picker.liquid: the radios carry
   * `data-option-position`, and are named `{form_id}-option{position}`.
   */
  #isColourInput(el) {
    if (!this.#colorPosition) return false;
    if (Number(el.dataset.optionPosition) === this.#colorPosition) return true;
    return (
      typeof el.name === "string" &&
      el.name.endsWith(`-option${this.#colorPosition}`)
    );
  }

  /**
   * Radios use the `form` attribute, so they sit inside <variant-picker>
   * rather than inside the <form>. Scope by section id to avoid picking up a
   * quick-buy picker elsewhere on the page.
   */
  #pickerScope() {
    return (
      document.querySelector(
        `variant-picker[section-id="${this.dataset.sectionId}"]`,
      ) ||
      this.#form ||
      document
    );
  }

  #colourFromDom() {
    if (!this.#colorPosition) return null;

    const pos = this.#colorPosition;
    const el = this.#pickerScope().querySelector(
      `input[data-option-position="${pos}"]:checked, input[name$="-option${pos}"]:checked`,
    );

    return el ? this.#data.byValueId?.[String(el.value)] ?? null : null;
  }

  /* --------------------------------------------------------- variant events */

  #onVariantEvent = (event) => {
    const variant =
      event.detail?.variant ?? event.detail?.data?.variant ?? event.detail;
    if (!variant || typeof variant !== "object") return;

    if (this.#rerenders) {
      const id = variant.id == null ? null : String(variant.id);
      if (id && id !== this.#lastVariantId) {
        this.#lastVariantId = id;
        this.#rerender(id);
      }
      return;
    }

    let colour = null;

    if (this.#colorPosition > 0 && variant[`option${this.#colorPosition}`]) {
      colour = variant[`option${this.#colorPosition}`];
    } else if (Array.isArray(variant.options) && this.#colorPosition > 0) {
      colour = variant.options[this.#colorPosition - 1];
    } else if (variant.id != null) {
      const index = this.#data.variants?.[String(variant.id)];
      colour = index == null ? null : this.#data.colourList?.[index];
    }

    if (colour) {
      this.#lastVariantId =
        variant.id != null ? String(variant.id) : this.#lastVariantId;
      this.#apply(colour);
    }
  };

  /* ---------------------------------------------------------------- applying */

  #apply(colour) {
    if (!colour || colour === this.#currentColour) return;
    this.#currentColour = colour;
    this.#applyImages(colour);
  }

  #applyImages(colour) {
    const entry = this.#data.colours?.[colour];
    if (!entry) return;

    const swatch = this.querySelector("[data-material-swatch]");
    if (swatch && entry.sm) {
      swatch.src = entry.sm;
      swatch.srcset = `${entry.sm} 1x, ${entry.sm2x} 2x`;
    }

    this.querySelectorAll("[data-material-hero]").forEach((img) => {
      if (!entry.lg) return;
      img.src = entry.lg;
      img.srcset = `${entry.lg} 800w, ${entry.lg2x} 1600w`;
      img.alt = entry.alt || img.alt;
    });

    this.dispatchEvent(
      new CustomEvent("material:colour:change", {
        bubbles: true,
        detail: { colour },
      }),
    );
  }

  /**
   * Opt-in path for stores where the material itself differs per variant.
   * One lightweight Section Rendering request per change, so copy, drawers and
   * images all stay consistent rather than drifting apart.
   */
  async #rerender(variantId) {
    this.#inflight?.abort();
    const controller = new AbortController();
    this.#inflight = controller;

    const url = `${this.dataset.productUrl}?variant=${variantId}&section_id=${this.dataset.sectionId}`;

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(response.statusText);

      const doc = new DOMParser().parseFromString(
        await response.text(),
        "text/html",
      );
      const next = doc.getElementById(this.id);
      if (!next || controller.signal.aborted || !this.isConnected) return;

      const wasOpen = this.querySelector("details")?.open;
      const openDrawer = this.querySelector("[data-material-drawer][open]")
        ?.dataset.materialDrawer;

      this.replaceWith(next);

      const details = next.querySelector("details");
      if (details && wasOpen) details.open = true;
      if (openDrawer) next.open?.(openDrawer);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.warn("[product-material] Section re-render failed", error);
    }
  }

  /* ----------------------------------------------------------------- drawers */

  #onClick = (event) => {
    const opener = event.target.closest("[data-material-open]");
    if (opener) {
      event.preventDefault();
      this.open(opener.dataset.materialOpen);
      return;
    }

    if (event.target.closest("[data-material-close]")) {
      event.preventDefault();
      this.close(event.target.closest("[data-material-drawer]"));
    }
  };

  #onDialogClick = (event) => {
    if (event.target.matches("[data-material-drawer]"))
      this.close(event.target);
  };

  #onCancel = (event) => {
    event.preventDefault();
    this.close(event.target);
  };

  open(name) {
    const dialog = this.querySelector(
      `[data-material-drawer="${CSS.escape(name)}"]`,
    );
    if (!dialog || dialog.open) return;

    dialog.showModal();
    document.documentElement.classList.add("material-drawer-open");
    requestAnimationFrame(() => dialog.classList.add("is-open"));

    this.dispatchEvent(
      new CustomEvent("material:drawer:open", {
        bubbles: true,
        detail: { name },
      }),
    );
  }

  close(dialog) {
    if (!dialog || !dialog.open) return;

    const finish = () => {
      dialog.close();
      document.documentElement.classList.remove("material-drawer-open");
    };

    dialog.classList.remove("is-open");

    if (!MOTION_SAFE) {
      finish();
      return;
    }

    let settled = false;
    const once = () => {
      if (settled) return;
      settled = true;
      dialog.removeEventListener("transitionend", once);
      finish();
    };

    dialog.addEventListener("transitionend", once);
    setTimeout(once, 400);
  }
}

if (!customElements.get("product-material")) {
  customElements.define("product-material", ProductMaterial);
}
